import styles from "./Graph.module.scss";
import logo from "Explore/Assets/logo.png";
import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { ZoomIn, ZoomOut } from "lucide-react";
import {
  AlternativeImage,
  ImageCheckType,
  ImageType,
} from "Explore/Config/ImageType";
import { Spin } from "antd";
import { Button, Tooltip } from "@mui/material";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import ZoomInMapIcon from "@mui/icons-material/ZoomInMap";
import InformationBoard from "./InformationBoard";
import classNames from "classnames";
import useDeviceCheck from "Hooks/useDeviceCheck";
import { IconPack } from "Explore/Config/Icons";
import InformationBoardMobile from "./InformationBoardMobile";

const minZoom = 0.5;
const maxZoom = 5;

const D3Graph = ({
  filter,
  data: _data,
  graphType = "faculty",
  isLoading,
  width,
  height,
  simpleMode,
}) => {
  const { isMobile } = useDeviceCheck();
  const svgRef = useRef(null);
  const gRef = useRef(null);
  const containerRef = useRef(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  const data = useMemo(
    () => ({
      nodes: _data.nodes ?? [],
      lines: (_data.lines ?? []).map((e) => ({
        ...e,
        source: e.from || e.source,
        target: e.to || e.target,
      })),
      max_edges: _data.max_edges ?? 0,
    }),
    [_data]
  );
  const { nodeSize, nodeFontSize } = useMemo(
    () => ({
      nodeSize: simpleMode ? 40 : 40,
      nodeFontSize: simpleMode ? 10 : 14,
    }),
    []
  );

  useEffect(() => {
    if (!svgRef.current) return;

    d3.select(svgRef.current).selectAll("*").remove();

    const updateDimensions = () => {
      const container = containerRef.current;
      if (!container) return;

      const width = container.clientWidth;
      const height = container.clientHeight;

      // Update SVG dimensions
      d3.select(svgRef.current).attr("width", width).attr("height", height);

      // Update simulation center force
      simulation.force("center", d3.forceCenter(width / 2, height / 2));
      simulation.alpha(0.3).restart();
    };

    // Create resize observer
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    // Initialize the graph
    const initialWidth = containerRef.current.clientWidth;
    const initialHeight = containerRef.current.clientHeight;

    const svg = d3
      .select(svgRef.current)
      .attr("width", initialWidth)
      .attr("height", initialHeight)
      .attr("class", styles["graph-svg"]);

    const g = svg.append("g");
    gRef.current = g;

    // Zoom behavior setup
    const zoom = d3
      .zoom()
      .scaleExtent([minZoom, maxZoom])
      .filter((event) => {
        return !event.ctrlKey && event.type !== 'wheel';
      })
      .on("zoom", (event) => {
        const k = event.transform.k;
        setZoomLevel(k);

        g.attr("transform", event.transform);
        g.selectAll(".node-group").each(function () {
          const scale = 1 / k;
          d3.select(this)
            .selectAll(".keep-size")
            .attr("transform", `scale(${scale})`);
        });
      });

    svg.call(zoom);

    // Arrow marker definition
    // svg.append("defs").selectAll("marker")
    //   .data(["end"])
    //   .join("marker")
    //   .attr("id", "arrow")
    //   .attr("viewBox", "0 -5 10 10")
    //   .attr("refX", 30)
    //   .attr("refY", 0)
    //   .attr("markerWidth", 6)
    //   .attr("markerHeight", 6)
    //   .attr("orient", "auto")
    //   .append("path")
    //   .attr("fill", "#999")
    //   .attr("d", "M0,-5L10,0L0,5");

    const simulation = d3
      .forceSimulation(data.nodes)
      .force(
        "link",
        d3
          .forceLink(data.lines)
          .id((d) => d.id)
          .distance(Math.min(initialWidth, initialHeight) * 0.35)
      )
      .force("charge", d3.forceManyBody().strength(-300))
      .force("center", d3.forceCenter(initialWidth / 2, initialHeight / 2))
      .force("collision", d3.forceCollide().radius(nodeSize * 1.25));

    // Links with click handling
    const lines = g
      .append("g")
      .selectAll("line")
      .data(data.lines)
      .join("line")
      .attr("stroke", "var(--light-grey)")
      .attr("stroke-opacity", (d) => d.edge_type === 'co_collaborator' ? 0.8 : 1)
      .attr("stroke-dasharray", (d) => d.edge_type === 'co_collaborator' ? "5,5" : "none")
      .attr("stroke-width", (d) =>
        Math.max(3, Math.min(10, Math.ceil((d.weight || 1) / (graphType == "unit" ? 10 : 5))))
      )
      // .attr("marker-end", "url(#arrow)")
      .attr("cursor", "pointer")
      .on("click", function (event, d) {
        event.stopPropagation();

        // Deselect all links
        lines
          .attr("stroke", "var(--light-grey)")
          .attr("stroke-opacity", (l) => l.edge_type === 'co_collaborator' ? 0.8 : 1)
          .classed("active", false);

        d3.select(this)
          .attr("stroke", "var(--accent-color)")
          .attr("stroke-opacity", 1)
          .classed("active", true)
          .raise();

        // Highlight the nodes connected to the clicked link
        nodes
          .select(".border")
          .attr("stroke", "var(--light-grey)")
          .attr("stroke-width", 2)
          .classed("active", false);
        nodes
          .filter((n) => n.id === d.source.id || n.id === d.target.id)
          .select(".border")
          .attr("stroke", "var(--accent-color)")
          .attr("stroke-width", 3)
          .classed("active", true);

        setSelectedElement({
          ...d,
          type: "line",
          fromNode: data.nodes.find((n) => n.id === d.source.id),
          toNode: data.nodes.find((n) => n.id === d.target.id),
        });
      });

    // Link tooltips
    lines.append("title")
      .text((d) => d.edge_type === 'co_collaborator' ? `Shared papers: ${d.shared_papers ?? 0}` : `Joint publications: ${d.weight}`);

    // Link hover effects
    lines
      .on("mouseover", function () {
        if (!d3.select(this).classed("active")) {
          d3.select(this).attr("stroke", "var(--accent-color)");
        }
      })
      .on("mouseout", function () {
        if (!d3.select(this).classed("active")) {
          d3.select(this).attr("stroke", "var(--light-grey)");
        }
      });

    // Nodes with click handling
    const nodes = g
      .append("g")
      .selectAll("g")
      .data(data.nodes)
      .join("g")
      .attr("class", "node-group")
      .call(drag(simulation))
      .on("click", function (event, d) {
        event.stopPropagation();

        // Highlight the clicked node
        nodes
          .select(".border")
          .attr("stroke", "var(--light-grey)")
          .attr("stroke-width", 2)
          .classed("active", false);

        d3.select(this)
          .select(".border")
          .attr("stroke", "var(--accent-color)")
          .attr("stroke-width", 3)
          .classed("active", true);

        // Highlight the links connected to the clicked node
        lines
          .attr("stroke", "var(--light-grey)")
          .attr("stroke-opacity", (l) => l.edge_type === 'co_collaborator' ? 0.8 : 1)
          .classed("active", false);

        lines
          .filter((line) => line.source.id === d.id || line.target.id === d.id)
          .attr("stroke", "var(--accent-color)")
          .attr("stroke-opacity", 1)
          .classed("active", true)
          .raise();

        setSelectedElement({
          ...d.data,
          id: d.id,
          type: "node",
        });
      });

    // Node visual elements
    nodes
      .append("clipPath")
      .attr("id", (d) => `clip-${d.id}`)
      .append("circle")
      .attr("r", nodeSize / 2);

    nodes
      .append("circle")
      .attr("class", "keep-size border")
      .attr("r", nodeSize / 2 + 1)
      .attr("fill", "#fff")
      .attr("stroke", "var(--light-grey)")
      .attr("stroke-width", 2);

    nodes
      .append("image")
      .attr("class", "keep-size")
      .attr("href", (d) =>
        graphType === "unit"
          ? `https://media.academic.online/institutions_images/${d.data?.institution_id}/${ImageType.SEAL}.png`
          : d.data.photo || AlternativeImage[ImageCheckType.FACULTY_AVATAR]
      )
      .attr("x", -nodeSize / 2)
      .attr("y", -nodeSize / 2)
      .attr("width", nodeSize)
      .attr("height", nodeSize)
      .attr("clip-path", (d) => `url(#clip-${d.id})`)
      .attr("cursor", "pointer")
      .on("error", function () {
        d3.select(this).attr(
          "href",
          graphType === "unit"
            ? logo
            : AlternativeImage[ImageCheckType.FACULTY_AVATAR]
        );
      });

    nodes
      .append("text")
      .attr("class", "keep-size")
      .text((d) => d.data.name)
      .attr("x", 0)
      .attr("y", nodeSize / 2 + 20)
      .attr("text-anchor", "middle")
      .attr("fill", "#333")
      .attr("font-size", `${nodeFontSize}px`)
      .call((selection) => {
        selection.each(function (d) {
          d.bbox = this.getBBox();
        });
      });

    nodes
      .insert("rect", "text")
      .attr("class", "keep-size")
      .attr("x", function (d) {
        return d.bbox.x - 4;
      })
      .attr("y", function (d) {
        return d.bbox.y - 4;
      })
      .attr("width", function (d) {
        return d.bbox.width + 8;
      })
      .attr("height", function (d) {
        return d.bbox.height + 8;
      })
      .attr("rx", 4)
      .attr("ry", 4)
      .style("fill", "rgb(255 255 255 / 70%)");

    // Hover effects
    nodes
      .on("mouseover", function (event, d) {
        if (!d3.select(this).select(".border").classed("active")) {
          d3.select(this)
            .select(".border")
            .attr("stroke", "rgb(from var(--accent-color) r g b / 60%)")
            .attr("stroke-width", 3);

          lines
            .filter(
              (line) => line.source.id === d.id || line.target.id === d.id
            )
            .attr("stroke", "var(--accent-color)");
        }
      })
      .on("mouseout", function (event, d) {
        if (!d3.select(this).select(".border").classed("active")) {
          d3.select(this)
            .select(".border")
            .attr("stroke", "var(--light-grey)")
            .attr("stroke-width", 2);

          lines
            .filter(
              (line) => line.source.id === d.id || line.target.id === d.id
            )
            .attr("stroke", "var(--light-grey)");
        }
      });

    // Clear selection when clicking on empty space
    svg.on("click", () => {
      lines
        .attr("stroke", "var(--light-grey)")
        .attr("stroke-opacity", (l) => l.edge_type === 'co_collaborator' ? 0.8 : 1)
        .classed("active", false);
      nodes
        .select(".border")
        .attr("stroke", "var(--light-grey)")
        .attr("stroke-width", 2)
        .classed("active", false);
      setSelectedElement(null);
    });

    // Simulation tick
    simulation.on("tick", () => {
      lines
        .attr("x1", (d) => d.source.x)
        .attr("y1", (d) => d.source.y)
        .attr("x2", (d) => d.target.x)
        .attr("y2", (d) => d.target.y);

      nodes.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });

    // Drag functions
    function drag(simulation) {
      function dragstarted(event) {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        event.subject.fx = event.subject.x;
        event.subject.fy = event.subject.y;
      }

      function dragged(event) {
        event.subject.fx = event.x;
        event.subject.fy = event.y;
      }

      function dragended(event) {
        if (!event.active) simulation.alphaTarget(0);
        event.subject.fx = null;
        event.subject.fy = null;
      }

      return d3
        .drag()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended);
    }

    return () => simulation.stop();
  }, [data]);

  useEffect(() => {
    // Add full-screen change event listeners
    const handleFullScreenChange = () => {
      setIsFullScreen(document.fullscreenElement !== null);
    };

    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, []);

  // Handle full-screen toggle
  const toggleFullScreen = async () => {
    try {
      if (!isFullScreen) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error("Error toggling fullscreen:", error);
    }
  };

  // Zoom control handlers
  const d3Zoom = d3.zoom().on("zoom", function (event) {
    gRef.current.attr("transform", event.transform);
    gRef.current.selectAll(".node-group").each(function () {
      const scale = 1 / event.transform.k;
      d3.select(this)
        .selectAll(".keep-size")
        .attr("transform", `scale(${scale})`);
    });
  });

  const handleCenter = () => {
    d3.select(svgRef.current)
      .transition()
      .duration(750)
      .call(d3Zoom.transform, d3.zoomIdentity);
  };

  const handleZoomIn = () => {
    d3.select(svgRef.current)
      .transition()
      .duration(750)
      .call(d3Zoom.scaleBy, 1.5);
  };

  const handleZoomOut = () => {
    d3.select(svgRef.current)
      .transition()
      .duration(750)
      .call(d3Zoom.scaleBy, 1 / 1.5);
  };

  return (
    <div
      ref={containerRef}
      style={{
        height: isFullScreen ? "100vh" : height || "80vh",
        width: width || "100%",
        backgroundColor: isFullScreen
          ? "var(--background-color-2)"
          : "transparent",
      }}
      className={classNames(styles["graph-container"], {
        [styles["simple"]]: simpleMode,
      })}
    >
      {/* Loading */}
      {isLoading && (
        <div className={styles["loading"]}>
          <Spin />
        </div>
      )}

      {/* Control Panel */}
      {!simpleMode && (
        <div className={styles["control-panel"]}>
          <Tooltip title="Zoom In" placement="left" arrow>
            <Button disabled={zoomLevel >= maxZoom} onClick={handleZoomIn}>
              <ZoomIn />
            </Button>
          </Tooltip>
          <Tooltip title="Zoom Out" placement="left" arrow>
            <Button disabled={zoomLevel <= minZoom} onClick={handleZoomOut}>
              <ZoomOut />
            </Button>
          </Tooltip>
          <Tooltip title="Center View" placement="left" arrow>
            <Button onClick={handleCenter}>
              <CenterFocusStrongIcon />
            </Button>
          </Tooltip>
          <Tooltip
            title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
            placement="left"
            arrow
          >
            <Button onClick={toggleFullScreen}>
              {isFullScreen ? <ZoomInMapIcon /> : <ZoomOutMapIcon />}
            </Button>
          </Tooltip>
        </div>
      )}

      {/* Graph */}
      <svg ref={svgRef} />

      {/* Info Panel */}
      {!simpleMode && (
        <div className={styles["information-board__container"]}>
          {isMobile ? (
            <InformationBoardMobile
              filter={filter}
              maxLines={data.max_edges}
              selectedElement={selectedElement}
              graphType={graphType}
            />
          ) : (
            <InformationBoard
              filter={filter}
              maxLines={data.max_edges}
              selectedElement={selectedElement}
              graphType={graphType}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default D3Graph;
